import React, { useEffect } from "react"
import { observer } from "mobx-react"
import styled, { withTheme } from "styled-components"
import Dropzone from "react-dropzone"

import useInject from "~/hooks/useInject"

import RegistrationCard from "~/components/svg/RegistrationCard"
import MountainIcon from "~/components/svg/MountainIcon"
import ParkingPermit from "~/components/svg/ParkingPermit"
import CirclePlusIcon from "~/components/svg/CirclePlusIcon"
import GrayButton from "./GrayButton"
import ImageMenu from "~/components/traffic-tickets/steps/BasicDetails/ImageContextMenu"

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 515px;
`

const ButtonContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 100px;
  margin: 0.5em;
  border: ${props =>
    props.isImage ? `1px solid ${props.theme.colors.ui.inputBorder}` : null};
  border-radius: 4px;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.primary};
`

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 110px;
  height: 90px;
  margin-left: 5px;
`

const PreviewImage = styled.img`
  max-height: 100%;
  max-width: 100%;
`

const ImagePreview = ({ base64, alt, item, id }) => {
  return (
    <ButtonContainer isImage>
      <ImageMenu
        items={[
          {
            text: "Delete",
            onClick: () => item.removeLocalEvidenceImage(id),
            serious: true
          }
        ]}
      >
        <ImageContainer>
          <PreviewImage src={base64} alt={alt} />
        </ImageContainer>
      </ImageMenu>
    </ButtonContainer>
  )
}

const EvidenceIcon = withTheme(({ theme }) => (
  <MountainIcon color={theme.colors.primary} />
))
const PermitIcon = withTheme(({ theme }) => (
  <ParkingPermit color={theme.colors.primary} />
))
const RegistrationIcon = withTheme(({ theme }) => (
  <RegistrationCard color={theme.colors.primary} />
))
const AddMoreIcon = withTheme(({ theme }) => (
  <CirclePlusIcon color={theme.colors.primary} />
))

const mapStore = store => ({
  plate: store.parkingFlow.flow.plate
})

const EvidenceModalButtons = ({ ticket }) => {
  const { plate } = useInject(mapStore)
  const { persistedEvidenceImages } = ticket

  useEffect(() => {
    if (plate) plate.fetchImages()
  }, [])

  if (!plate) return null

  const persistedPlateRegistration = plate.registrationImage
  const persistedPlatePermit = plate.parkingPermitImage
  return (
    <ButtonRow>
      {persistedEvidenceImages.map(evidence => (
        <ImagePreview
          item={ticket}
          id={evidence.id}
          key={evidence.id}
          base64={evidence.base64EncodedImage}
          alt="Ticket Evidence Preview"
        />
      ))}
      {persistedEvidenceImages.length < 3 ? (
        <Dropzone
          multiple={false}
          onDrop={ticket.handleEvidenceImageDrop}
          accept="image/*"
        >
          {({ getRootProps, getInputProps }) => (
            <ButtonContainer>
              <GrayButton
                {...getRootProps({ onClick: evt => evt.preventDefault() })}
                Svg={
                  persistedEvidenceImages.length < 1
                    ? EvidenceIcon
                    : AddMoreIcon
                }
                error={false}
                fullHeight
                column
                noMargin
              >
                <input {...getInputProps()} />
                Add an evidence
              </GrayButton>
            </ButtonContainer>
          )}
        </Dropzone>
      ) : null}
      {!persistedPlateRegistration ? (
        <Dropzone
          multiple={false}
          onDrop={files => {
            plate.handleDocumentImageDrop(files, "registrationImage")
          }}
          accept="image/*"
        >
          {({ getRootProps, getInputProps }) => (
            <ButtonContainer>
              <GrayButton
                {...getRootProps({ onClick: evt => evt.preventDefault() })}
                Svg={RegistrationIcon}
                error={false}
                fullHeight
                column
                noMargin
              >
                <input {...getInputProps()} />
                Add your registration card
              </GrayButton>
            </ButtonContainer>
          )}
        </Dropzone>
      ) : (
        <ImagePreview
          item={plate}
          id="registrationImage"
          key={persistedPlateRegistration.id}
          base64={persistedPlateRegistration.base64EncodedImage}
          alt="Registration Preview"
        />
      )}
      {!persistedPlatePermit ? (
        <Dropzone
          multiple={false}
          onDrop={files => {
            plate.handleDocumentImageDrop(files, "parkingPermitImage")
          }}
          accept="image/*"
        >
          {({ getRootProps, getInputProps }) => (
            <ButtonContainer>
              <GrayButton
                {...getRootProps({ onClick: evt => evt.preventDefault() })}
                Svg={PermitIcon}
                error={false}
                fullHeight
                column
                noMargin
              >
                <input {...getInputProps()} />
                Add your parking permit
              </GrayButton>
            </ButtonContainer>
          )}
        </Dropzone>
      ) : (
        <ImagePreview
          item={plate}
          id="parkingPermitImage"
          key={persistedPlatePermit.id}
          base64={persistedPlatePermit.base64EncodedImage}
          alt="Parking Permit Preview"
        />
      )}
    </ButtonRow>
  )
}

export default observer(EvidenceModalButtons)
